import { CheckCircle2, Copy, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useReducer } from "react";
import { toast } from "sonner";

import { getApiErrorMessage } from "@/shared/api/apiError";
import { OffCanvas } from "@/shared/components/OffCanvas";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";

import {
  useConfirmarAtivacaoMfaMutation,
  useIniciarAtivacaoMfaMutation,
} from "../../services/meuPerfilApi";

interface Ativar2FAOffCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (recoveryCodes: string[]) => void;
}

/**
 * Extrai o segredo Base32 do URI otpauth retornado pelo back.
 * Formato: otpauth://totp/Issuer:email?secret=BASE32&issuer=Issuer
 */
function extractSecret(otpAuthUri: string): string {
  const match = otpAuthUri.match(/[?&]secret=([^&]+)/i);
  return match ? match[1] : "";
}

type State = {
  secret: string;
  totpUrl: string;
  code: string;
  error: string;
  loadError: string;
  copiedSecret: boolean;
};

type Action =
  | { type: "reset" }
  | { type: "set_totp"; totpUrl: string; secret: string }
  | { type: "set_load_error"; message: string }
  | { type: "set_code"; code: string }
  | { type: "set_error"; message: string }
  | { type: "set_copied_secret"; value: boolean };

const initialState: State = {
  secret: "",
  totpUrl: "",
  code: "",
  error: "",
  loadError: "",
  copiedSecret: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "reset":
      return initialState;
    case "set_totp":
      return { ...state, totpUrl: action.totpUrl, secret: action.secret };
    case "set_load_error":
      return { ...state, loadError: action.message };
    case "set_code":
      return { ...state, code: action.code, error: "" };
    case "set_error":
      return { ...state, error: action.message };
    case "set_copied_secret":
      return { ...state, copiedSecret: action.value };
  }
}

export function Ativar2FAOffCanvas({
  isOpen,
  onClose,
  onSuccess,
}: Ativar2FAOffCanvasProps) {
  const [{ secret, totpUrl, code, error, loadError, copiedSecret }, dispatch] =
    useReducer(reducer, initialState);

  const [iniciarAtivacao, { isLoading: isLoadingSecret }] =
    useIniciarAtivacaoMfaMutation();
  const [confirmarAtivacao, { isLoading: loading }] =
    useConfirmarAtivacaoMfaMutation();

  useEffect(() => {
    if (!isOpen) return;

    dispatch({ type: "reset" });

    iniciarAtivacao()
      .unwrap()
      .then((response) => {
        dispatch({
          type: "set_totp",
          totpUrl: response.qrCodeUri,
          secret: extractSecret(response.qrCodeUri),
        });
      })
      .catch((err) => {
        dispatch({
          type: "set_load_error",
          message: getApiErrorMessage(
            err,
            "Não foi possível iniciar a ativação do 2FA. Tente novamente."
          ),
        });
      });
  }, [isOpen, iniciarAtivacao]);

  const handleCopySecret = () => {
    if (!secret) return;

    const copy = () => {
      if (navigator.clipboard) {
        return navigator.clipboard.writeText(secret);
      }
      // Fallback para ambientes sem HTTPS (ex: QA em HTTP)
      const textarea = document.createElement("textarea");
      textarea.value = secret;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      return Promise.resolve();
    };

    copy()
      .then(() => {
        dispatch({ type: "set_copied_secret", value: true });
        toast.success("Chave copiada para a área de transferência");
        setTimeout(
          () => dispatch({ type: "set_copied_secret", value: false }),
          2000
        );
      })
      .catch(() => {
        toast.error("Não foi possível copiar a chave. Copie manualmente.");
      });
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").substring(0, 6);
    dispatch({ type: "set_code", code: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== 6) {
      dispatch({ type: "set_error", message: "Digite um código de 6 dígitos" });
      return;
    }

    dispatch({ type: "set_error", message: "" });

    try {
      const response = await confirmarAtivacao({ codigo: code }).unwrap();
      toast.success("2FA ativado com sucesso!");
      onSuccess(response.recoveryCodes);
      onClose();
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err,
          "Não foi possível ativar o 2FA. Tente novamente."
        )
      );
    }
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="outline-neutral" onClick={onClose} disabled={loading}>
        Cancelar
      </Button>
      <Button
        onClick={handleSubmit}
        disabled={code.length !== 6 || loading || isLoadingSecret || !secret}
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        Ativar
      </Button>
    </div>
  );

  return (
    <OffCanvas
      isOpen={isOpen}
      onClose={onClose}
      title="Ativar Autenticação em Dois Fatores"
      subtitle="Configure o 2FA para proteger sua conta"
      size="md"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Instruções */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm leading-relaxed text-gray-700">
            Escaneie o QR Code abaixo com seu aplicativo autenticador (Google
            Authenticator, Microsoft Authenticator ou similar) e insira o código
            gerado para confirmar a ativação.
          </p>
        </div>

        {loadError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-700">{loadError}</p>
          </div>
        )}

        {/* QR Code */}
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-[232px] w-[232px] items-center justify-center rounded-lg border-2 border-gray-200 bg-white p-4">
            {totpUrl ? (
              <QRCodeSVG
                value={totpUrl}
                size={200}
                level="M"
                includeMargin={false}
              />
            ) : (
              <Loader2
                size={32}
                className="animate-spin text-[var(--color-primary)]"
              />
            )}
          </div>
        </div>

        {/* Chave manual */}
        <div>
          <label className="mb-2 block text-sm text-gray-600">
            Chave Manual (caso não consiga escanear o QR Code)
          </label>
          <div className="flex gap-2">
            <div className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 font-mono text-sm break-all text-gray-900">
              {secret || "—"}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleCopySecret}
              disabled={!secret}
              title="Copiar chave"
              className="h-10 w-10 flex-shrink-0"
            >
              {copiedSecret ? (
                <CheckCircle2 size={18} className="text-green-600" />
              ) : (
                <Copy size={18} className="text-gray-600" />
              )}
            </Button>
          </div>
        </div>

        {/* Campo de código de confirmação */}
        <div>
          <label className="mb-2 block text-sm text-gray-700">
            Código de Confirmação <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={code}
            onChange={handleCodeChange}
            placeholder="000000"
            aria-invalid={!!error}
            className="h-11 text-center font-mono text-lg tracking-widest"
            maxLength={6}
          />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          {!error && (
            <p className="mt-2 text-xs text-gray-500">
              Código TOTP gerado pelo aplicativo autenticador (6 dígitos)
            </p>
          )}
        </div>
      </form>
    </OffCanvas>
  );
}
